import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
    CssBaseline,
    Fade,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    DeleteOutlined as DeleteIcon,
    PersonOff as PersonOffIcon,
    School as SchoolIcon, 
    AccountTree as SectionIcon, 
    MenuBook as SubjectIcon, 
    Close as CloseIcon,
} from '@mui/icons-material';
import { ThemeProvider, styled, alpha } from '@mui/material/styles'; 
import Lottie from 'lottie-react';

import axiosInstance from '../../api/axiosConfig';
import adminTheme from '../../theme';
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import animationData from '../../assets/icons/no-results.json';

// --- Styled Components ---

const ClassGroupContainer = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2.5),
    overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    boxShadow: 'none',
    backgroundColor: theme.palette.background.paper,
}));

const ClassHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.75),
    backgroundColor: '#F5F7FB',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
}));

const SectionsContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0, 2.75, 2.25, 2.75),
}));

const SectionBlock = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2.5),
}));

const AssignmentRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 0),
    '&:not(:last-of-type)': {
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    },
    '&:hover': {
        backgroundColor: alpha(theme.palette.action.hover, 0.4),
        borderRadius: theme.spacing(1),
        paddingLeft: theme.spacing(1.1),
        paddingRight: theme.spacing(1.1),
        marginLeft: theme.spacing(-1.1),
        marginRight: theme.spacing(-1.1),
    }
}));

const FadeTransition = React.forwardRef(function FadeTransition(props, ref) {
    return <Fade ref={ref} {...props} />;
});

// --- Utility ---
const getTeacherInitials = (firstName, lastName) =>
    `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

// --- Main Component ---
const TeacherSubjects = () => {
    const schoolId = localStorage.getItem('schoolId');

    // Data States
    const [subjects, setSubjects] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    
    // UI/UX States
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [searchQuery, setSearchQuery] = useState('');
    
    // Dialog States
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState('assign');
    const [currentAssignmentData, setCurrentAssignmentData] = useState(null);
    const [selectedTeacherId, setSelectedTeacherId] = useState('');
    
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [unassignTarget, setUnassignTarget] = useState({ id: null, label: '' });
    const [actionLoading, setActionLoading] = useState(false);

    // --- Helpers ---
    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    // --- Data Fetching ---
    const fetchAssignments = useCallback(async () => {
        if (!schoolId) return;
        try {
            const response = await axiosInstance.get(`/api/subject-teacher-assignments/school/${schoolId}`);
            setAssignments(response.data?.data || response.data || []);
        } catch (err) {
            setError("Could not refresh assignments list.");
        }
    }, [schoolId]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        if (!schoolId) {
            setError('School ID not found. Please ensure you are logged in.');
            setLoading(false);
            return;
        }
        try {
            const results = await Promise.allSettled([
                axiosInstance.get('/api/subjects', { params: { schoolId } }),
                axiosInstance.get(`/api/subject-teacher-assignments/school/${schoolId}`),
                axiosInstance.get('/api/employees', { params: { role: 'TEACHER', schoolId } }),
                axiosInstance.get('/api/classes', { headers: { schoolId } }),
                axiosInstance.get('/api/sections', { headers: { schoolId } }),
            ]);

            const [ subjectsRes, assignmentsRes, teachersRes, classesRes, sectionsRes ] = results;

            if (subjectsRes.status === 'fulfilled') setSubjects(subjectsRes.value.data?.data || []);
            if (assignmentsRes.status === 'fulfilled') setAssignments(assignmentsRes.value.data?.data || assignmentsRes.value.data || []);
            if (teachersRes.status === 'fulfilled') setTeachers(teachersRes.value.data?.data || teachersRes.value.data || []);
            if (classesRes.status === 'fulfilled') setClasses(classesRes.value.data?.filter(cls => cls.status === 'Active') || []);
            if (sectionsRes.status === 'fulfilled') setSections(sectionsRes.value.data?.filter(sec => sec.status === 'Active') || []);

            if (results.some(res => res.status === 'rejected')) {
                setError('Some essential data could not be loaded. Functionality may be limited.');
            }
        } catch (err) {
            setError('An unexpected error occurred while fetching data.');
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- Data Processing ---
    const getSectionsForClass = useCallback((classId) => {
        const cls = classes.find(c => c.classId === classId);
        if (!cls || !cls.assignedSections) return [];
        const assignedSectionIds = cls.assignedSections.split(',').map(Number);
        return sections.filter(sec => assignedSectionIds.includes(sec.sectionId));
    }, [classes, sections]);

    const groupedAndFilteredData = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        const flatList = [];

        classes.forEach(cls => {
            const classSections = getSectionsForClass(cls.classId);
            subjects.forEach(subject => {
                classSections.forEach(sec => {
                    const assignment = assignments.find(a => a.subjectId === subject.subjectId && a.classId === cls.classId && a.sectionId === sec.sectionId);
                    const teacher = assignment ? teachers.find(t => t.employeeId === assignment.teacherId) : null;

                    const rowData = {
                        subjectId: subject.subjectId,
                        subjectName: subject.subjectName,
                        classId: cls.classId,
                        className: cls.className,
                        sectionId: sec.sectionId,
                        sectionName: sec.sectionName,
                        teacherId: teacher?.employeeId || null,
                        teacherName: teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Unassigned',
                        assignmentId: assignment?.id || null,
                        firstName: teacher?.firstName || '',
                        lastName: teacher?.lastName || '',
                    };

                    const teacherNameToSearch = teacher ? `${teacher.firstName} ${teacher.lastName}` : 'unassigned';
                    if (!query ||
                        rowData.subjectName.toLowerCase().includes(query) ||
                        rowData.className.toLowerCase().includes(query) ||
                        rowData.sectionName.toLowerCase().includes(query) ||
                        teacherNameToSearch.toLowerCase().includes(query))
                    {
                        flatList.push(rowData);
                    }
                });
            });
        });

        const grouped = flatList.reduce((acc, row) => {
            if (!acc[row.classId]) acc[row.classId] = { classId: row.classId, className: row.className, sections: {} };
            if (!acc[row.classId].sections[row.sectionId]) acc[row.classId].sections[row.sectionId] = { sectionId: row.sectionId, sectionName: row.sectionName, subjects: [] };
            
            acc[row.classId].sections[row.sectionId].subjects.push(row);
            acc[row.classId].sections[row.sectionId].subjects.sort((a, b) => a.subjectName.localeCompare(b.subjectName));
            return acc;
        }, {});

        return Object.values(grouped)
            .sort((a, b) => a.className.localeCompare(b.className, undefined, { numeric: true }))
            .map(classGroup => ({
                ...classGroup,
                sections: Object.values(classGroup.sections).sort((a, b) => a.sectionName.localeCompare(b.sectionName)),
            }));
    }, [classes, subjects, assignments, teachers, searchQuery, getSectionsForClass]);

    // --- Handlers ---
    const handleOpenDialog = (type, assignmentDetails) => {
        setDialogType(type);
        setCurrentAssignmentData(assignmentDetails);
        setSelectedTeacherId(assignmentDetails?.teacherId || '');
        setError(null);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        if (!actionLoading) {
            setDialogOpen(false);
            setCurrentAssignmentData(null);
            setSelectedTeacherId('');
            setError(null);
        }
    };

    const handleAssignOrUpdateTeacher = async () => {
        if (!selectedTeacherId) return setError('Please select a teacher.');
        if (!currentAssignmentData || !schoolId) return setError('Missing required information.');
        
        const teacher = teachers.find(t => t.employeeId === selectedTeacherId);
        if (!teacher) return setError('Selected teacher not found.');

        setActionLoading(true);
        setError(null);

        const payload = {
            schoolId: Number(schoolId),
            id: currentAssignmentData.assignmentId,
            subjectId: currentAssignmentData.subjectId,
            classId: currentAssignmentData.classId,
            sectionId: currentAssignmentData.sectionId,
            teacherId: selectedTeacherId,
            teacherName: `${teacher.firstName} ${teacher.lastName}`,
        };
        const apiUrl = `/api/subject-teacher-assignments/school/${schoolId}`;

        try {
            if (dialogType === 'assign' || !payload.id) {
                const { id, ...postPayload } = payload; 
                await axiosInstance.post(apiUrl, postPayload);
                showSnackbar('Teacher assigned successfully.');
            } else {
                await axiosInstance.put(`${apiUrl}/${payload.id}`, payload);
                showSnackbar('Assignment updated successfully.');
            }
            handleCloseDialog();
            await fetchAssignments();
        } catch (err) {
            setError(`Failed to ${dialogType}. ${err.response?.data?.message || 'Please try again.'}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnassignClick = (assignmentId, label) => {
        if (!assignmentId) return setError("Cannot unassign: Invalid assignment ID.");
        setUnassignTarget({ id: assignmentId, label });
        setConfirmDialogOpen(true);
    };

    const confirmUnassign = async () => {
        if (!unassignTarget.id || !schoolId) return setError('Missing required information for unassignment.');
        setActionLoading(true);
        try {
            await axiosInstance.delete(`/api/subject-teacher-assignments/school/${schoolId}/${unassignTarget.id}`);
            showSnackbar('Teacher unassigned successfully.');
            await fetchAssignments();
            setConfirmDialogOpen(false);
        } catch (err) {
            setError(`Failed to unassign teacher. ${err.response?.data?.message || 'Please try again.'}`);
        } finally {
            setActionLoading(false);
            setUnassignTarget({ id: null, label: '' });
        }
    };

    // --- Render ---
    return (
        <ThemeProvider theme={adminTheme}>
            <CssBaseline />

            <PageContainer sx={{ width: '100%' }}>
                <AdminPageHero
                    title="Teacher Subject Mapping"
                    subtitle="Assign each subject to the right teacher by class and section."
                    metrics={[
                        { label: 'Classes', value: classes.length },
                        { label: 'Teachers', value: teachers.length },
                        { label: 'Subjects', value: subjects.length },
                        { label: 'Assignments', value: assignments.length },
                    ]}
                    sx={{ mb: 3 }}
                />

                {error && !dialogOpen && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <AdminPanelCard
                    title="Teacher Assignment Directory"
                    subtitle="Search assignments by subject, class, section, or teacher and update mappings quickly."
                    sx={{ mb: 3 }}
                >
                    <TextField
                        placeholder="Search by subject, class, section, or teacher..."
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            width: '100%',
                            maxWidth: 640,
                            "& .MuiOutlinedInput-root": { bgcolor: "background.paper" }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </AdminPanelCard>

                {/* Main Content Area */}
                {loading ? (
                    <AdminPanelCard title="Teacher Assignments" subtitle="Loading class-wise subject mapping.">
                        <Stack spacing={2}>
                            {Array.from({ length: 3 }).map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box sx={{ height: 58, bgcolor: '#F5F7FB', borderBottom: '1px solid', borderColor: 'divider' }} />
                                    <Box sx={{ p: 2.5 }}>
                                        <Stack spacing={1.25}>
                                            {Array.from({ length: 3 }).map((__, rowIndex) => (
                                                <Box
                                                    key={rowIndex}
                                                    sx={{
                                                        height: 48,
                                                        borderRadius: 2,
                                                        bgcolor: alpha('#94A3B8', 0.14),
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </AdminPanelCard>
                ) : groupedAndFilteredData.length === 0 ? (
                    
                    /* --- EMPTY STATE --- */
                    <AdminPanelCard>
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                            <Box width={150} height={150} mb={2} opacity={0.8}>
                                {animationData ? (
                                    <Lottie animationData={animationData} loop autoplay style={{ height: "100%", width: "100%" }} />
                                ) : (
                                    <SearchIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                                )}
                            </Box>
                            <Typography variant="h6" color="text.primary" fontWeight="bold" gutterBottom>
                                {searchQuery ? "No matches found" : "No assignments found"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1} textAlign="center" maxWidth={450}>
                                {searchQuery ? "Try refining your search terms." : "Ensure classes, sections, subjects, and teachers are configured, or check active statuses."}
                            </Typography>
                        </Box>
                    </AdminPanelCard>

                ) : (
                    
                    /* --- DATA VIEW --- */
                    <AdminPanelCard
                        title="Class-wise Subject Assignments"
                        subtitle="Review teacher ownership for every subject grouped by class and section."
                    >
                        <Stack spacing={2}>
                            {groupedAndFilteredData.map((classGroup) => (
                                <ClassGroupContainer key={classGroup.classId} variant="outlined">
                                    <ClassHeader>
                                        <SchoolIcon color="primary" />
                                        <Typography variant="h6" fontWeight={700} color="text.primary">
                                            {classGroup.className}
                                        </Typography>
                                    </ClassHeader>

                                    <SectionsContainer>
                                        {classGroup.sections.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                                                No sections match the current filter.
                                            </Typography>
                                        ) : (
                                            classGroup.sections.map((section) => (
                                                <SectionBlock key={section.sectionId}>
                                                    <Stack direction="row" alignItems="center" sx={{ mb: 1.75 }}>
                                                        <Chip
                                                            icon={<SectionIcon fontSize="small" />}
                                                            label={section.sectionName}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: alpha(adminTheme.palette.secondary.main, 0.1),
                                                                color: 'secondary.dark',
                                                                fontWeight: 700,
                                                                borderRadius: 1.5,
                                                                px: 0.75
                                                            }}
                                                        />
                                                    </Stack>

                                                    {section.subjects.length === 0 ? (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 2, py: 1 }}>
                                                            No subjects match the current filter.
                                                        </Typography>
                                                    ) : (
                                                        <Box sx={{ pl: { xs: 0, sm: 1 } }}>
                                                            {section.subjects.map((subjectRow) => (
                                                                <AssignmentRow key={subjectRow.subjectId}>
                                                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                                                                        <SubjectIcon sx={{ color: 'text.disabled', fontSize: '1.2rem' }} />
                                                                        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                                                                            {subjectRow.subjectName}
                                                                        </Typography>
                                                                    </Stack>

                                                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                                                                        {subjectRow.teacherId ? (
                                                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                                                                <Tooltip title={subjectRow.teacherName}>
                                                                                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 28, height: 28, fontSize: '0.75rem', fontWeight: 600 }}>
                                                                                        {getTeacherInitials(subjectRow.firstName, subjectRow.lastName)}
                                                                                    </Avatar>
                                                                                </Tooltip>
                                                                                <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 500 }} noWrap>
                                                                                    {subjectRow.teacherName}
                                                                                </Typography>
                                                                            </Stack>
                                                                        ) : (
                                                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled', fontStyle: 'italic', mr: 0.5 }}>
                                                                                <PersonOffIcon fontSize="small" />
                                                                                <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                                                                    Unassigned
                                                                                </Typography>
                                                                            </Stack>
                                                                        )}

                                                                        <Box display="flex" gap={0.5}>
                                                                            <Tooltip title={subjectRow.teacherId ? 'Edit Assignment' : 'Assign Teacher'}>
                                                                                <IconButton color="primary" size="small" onClick={() => handleOpenDialog(subjectRow.teacherId ? 'edit' : 'assign', subjectRow)}>
                                                                                    {subjectRow.teacherId ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            {subjectRow.teacherId && subjectRow.assignmentId && (
                                                                                <Tooltip title="Unassign Teacher">
                                                                                    <IconButton color="error" size="small" onClick={() => handleUnassignClick(subjectRow.assignmentId, `${subjectRow.className} / ${subjectRow.sectionName} - ${subjectRow.subjectName}`)}>
                                                                                        <DeleteIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>
                                                                    </Stack>
                                                                </AssignmentRow>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </SectionBlock>
                                            ))
                                        )}
                                    </SectionsContainer>
                                </ClassGroupContainer>
                            ))}
                        </Stack>
                    </AdminPanelCard>
                )}

                {/* --- Assign/Edit Dialog --- */}
                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
                    <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{dialogType === 'edit' ? 'Edit Assignment' : 'Assign Teacher'}</span>
                        <IconButton onClick={handleCloseDialog} size="small" disabled={actionLoading}><CloseIcon /></IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {currentAssignmentData && (
                            <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary" mb={0.5}>
                                    Class: <Typography component="span" fontWeight={600} color="text.primary">{currentAssignmentData.className}</Typography>
                                    {' • '}
                                    Section: <Typography component="span" fontWeight={600} color="text.primary">{currentAssignmentData.sectionName}</Typography>
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                                    Subject: {currentAssignmentData.subjectName}
                                </Typography>
                            </Box>
                        )}
                        
                        <FormControl fullWidth variant="outlined" disabled={actionLoading}>
                            <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>Select Teacher</InputLabel>
                            <Select
                                value={selectedTeacherId}
                                label="Select Teacher"
                                onChange={e => setSelectedTeacherId(e.target.value)}
                            >
                                <MenuItem value=""><em>-- Select a Teacher --</em></MenuItem>
                                {teachers.length > 0 ? (
                                    teachers.map(teacher => (
                                        <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                                            {`${teacher.firstName} ${teacher.lastName}`}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>No teachers available</MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        {error && dialogOpen && (
                            <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={handleCloseDialog} color="inherit" disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button onClick={handleAssignOrUpdateTeacher} variant="contained" color="primary" disableElevation disabled={!selectedTeacherId || actionLoading}>
                            {actionLoading ? <CircularProgress size={24} color="inherit" /> : (dialogType === 'edit' ? 'Update Assignment' : 'Assign Teacher')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* --- Confirm Unassign Dialog --- */}
                <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
                    <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Unassignment</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2" color="text.primary">
                            Are you sure you want to unassign the teacher from:
                        </Typography>
                        <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mt: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                            {unassignTarget.label}
                        </Typography>
                        <Typography variant="caption" color="error.main" sx={{ mt: 2, display: 'block' }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={() => setConfirmDialogOpen(false)} color="inherit" disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button onClick={confirmUnassign} color="error" variant="contained" disableElevation disabled={actionLoading}>
                            {actionLoading ? <CircularProgress size={24} color="inherit" /> : 'Unassign Teacher'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* --- Global Snackbar --- */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={(e, reason) => { if (reason !== 'clickaway') setSnackbar(prev => ({ ...prev, open: false })) }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%', color: '#fff', fontWeight: 500, borderRadius: 2 }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </PageContainer>
        </ThemeProvider>
    );
};

export default TeacherSubjects;
